import "../MuseoSansBinary.js"
import { createLetterPdf } from "../pdfUtils"

function getContainedLogoSize(imageWidth, imageHeight, maxWidth, maxHeight) {
  if (!imageWidth || !imageHeight) {
    return { width: maxWidth, height: maxHeight }
  }
  const widthRatio = maxWidth / imageWidth
  const heightRatio = maxHeight / imageHeight
  const scale = Math.min(widthRatio, heightRatio, 1)
  return {
    width: imageWidth * scale,
    height: imageHeight * scale,
  }
}

function drawReportLogo(doc, logoDataUri, imageWidth, imageHeight, x, y, maxWidth, maxHeight) {
  if (!logoDataUri) return
  try {
    const size = getContainedLogoSize(imageWidth, imageHeight, maxWidth, maxHeight)
    const drawX = x + (maxWidth - size.width) / 2
    const drawY = y + (maxHeight - size.height) / 2
    doc.addImage(logoDataUri, drawX, drawY, size.width, size.height)
  } catch (_err) {
    // Ignore logo rendering failures so report formatting remains stable.
  }
}

function formatFileName(meetName, meetDate) {
  const cleanName = (meetName || "MeetSwimmerSummary").replace(/[^a-zA-Z0-9]+/g, "_")
  const cleanDate = (meetDate || "").replace(/[^0-9-]+/g, "")
  if (cleanDate) {
    return `MeetSwimmerSummary_${cleanName}_${cleanDate}.pdf`
  }
  return `MeetSwimmerSummary_${cleanName}.pdf`
}

function getAgeGroupSortKey(label) {
  if (!label || typeof label !== "string") return Number.MAX_SAFE_INTEGER
  const match = label.match(/\d+/)
  if (!match) return Number.MAX_SAFE_INTEGER
  return parseInt(match[0], 10)
}

export default function generateMeetSwimmerSummaryPDF(data, options) {
  const { clubSummary, ageGroupBreakdown, meetName, meetDate, meetTotalsAllClubs } = data

  const doc = createLetterPdf()
  const margin = 10
  const contentWidth = doc.internal.pageSize.getWidth() - margin * 2
  const requestedColumns =
    options && typeof options.columns === "number" ? options.columns : 2
  const columns = requestedColumns === 3 ? 3 : 2
  const columnGap = 5
  const columnWidth = (contentWidth - (columns - 1) * columnGap) / columns
  const isThreeColumnLayout = columns === 3
  const ageGroupHeaderFontSize = isThreeColumnLayout ? 8 : 10
  const bodyFontSize = isThreeColumnLayout ? 7 : 8
  const columnStartY = 50
  let currentColumn = 0
  let yPos = 30

  // Compute report-level totals from clubSummary (the filtered set of clubs)
  let reportTotalSwimmers = 0
  let reportRelayOnlySwimmers = 0
  let reportExhibitionOnlySwimmers = 0
  ;(clubSummary || []).forEach((club) => {
    reportTotalSwimmers += club.totalSwimmers || 0
    reportRelayOnlySwimmers += club.relayOnlySwimmers || 0
    reportExhibitionOnlySwimmers += club.exhibitionOnlySwimmers || 0
  })

  const drawHeader = () => {
    doc.setFont("MuseoSansRounded-500", "normal")
    doc.setFontSize(12)

    drawReportLogo(
      doc,
      options?.logoDataUri || null,
      options?.logoWidth || null,
      options?.logoHeight || null,
      margin + contentWidth - 28,
      6,
      28,
      14,
    )

    const title = meetName || "Meet Swimmer Summary"
    const titleWidth = doc.getTextWidth(title)
    const titleX = margin + (contentWidth - titleWidth) / 2
    doc.text(title, titleX, 14)

    if (meetDate) {
      const dateText = String(meetDate).replace(/\//g, "-")
      const dateWidth = doc.getTextWidth(dateText)
      const dateX = margin + (contentWidth - dateWidth) / 2
      doc.text(dateText, dateX, 20)
    }

    doc.setFontSize(9)
    const subtitle = "Meet Swimmer Summary"
    const subtitleWidth = doc.getTextWidth(subtitle)
    const subtitleX = margin + (contentWidth - subtitleWidth) / 2
    doc.text(subtitle, subtitleX, 26)

    doc.setFontSize(7)
    doc.text("Powered by Swimmingly®", margin, 8)
  }

  const drawSummaryBlock = () => {
    const globalTotals = meetTotalsAllClubs || {
      totalSwimmers: reportTotalSwimmers,
      relayOnlySwimmers: reportRelayOnlySwimmers,
      exhibitionOnlySwimmers: reportExhibitionOnlySwimmers,
    }

    doc.setFont("MuseoSansRounded-500", "normal")
    doc.setFontSize(8)

    const lineA = `Total - Swimmers: ${globalTotals.totalSwimmers}  Relay Only: ${globalTotals.relayOnlySwimmers}  Exhibition Only: ${globalTotals.exhibitionOnlySwimmers}`
    const lineAWidth = doc.getTextWidth(lineA)
    const lineAX = margin + (contentWidth - lineAWidth) / 2
    doc.text(lineA, lineAX, yPos)
    yPos += 5

    const lineB = `Clubs in Report - Swimmers: ${reportTotalSwimmers}  Relay Only: ${reportRelayOnlySwimmers}  Exhibition Only: ${reportExhibitionOnlySwimmers}`
    const lineBWidth = doc.getTextWidth(lineB)
    const lineBX = margin + (contentWidth - lineBWidth) / 2
    doc.text(lineB, lineBX, yPos)
    yPos += 8

    if (clubSummary && clubSummary.length > 0) {
      const sections = [
        { key: "totalSwimmers", heading: "Total Swimmers" },
        { key: "relayOnlySwimmers", heading: "Relay-Only Swimmers" },
        { key: "exhibitionOnlySwimmers", heading: "Exhibition-Only Swimmers" },
      ]

      sections.forEach((section) => {
        const hasAny = clubSummary.some((c) => (c[section.key] || 0) > 0)
        if (!hasAny) return

        doc.setFont("MuseoSansRounded-700", "normal")
        doc.setFontSize(8)
        doc.text(section.heading, margin, yPos)
        const headingWidth = doc.getTextWidth(section.heading)
        doc.setLineWidth(0.2)
        doc.line(margin, yPos + 0.8, margin + headingWidth, yPos + 0.8)
        yPos += 4

        doc.setFont("MuseoSansRounded-500", "normal")
        doc.setFontSize(7)

        clubSummary.forEach((club) => {
          const value = String(club[section.key] || 0)
          const label = `${club.clubName}:`
          doc.text(label, margin, yPos)
          const valueWidth = doc.getTextWidth(value)
          const valueX = margin + columnWidth - valueWidth
          doc.text(value, valueX, yPos)
          yPos += 4
        })

        yPos += 4
      })
    }

    if (yPos < columnStartY) {
      yPos = columnStartY
    }
  }

  const checkPageHeight = (requiredSpace) => {
    const maxY = 280
    if (yPos + requiredSpace <= maxY) return
    yPos = columnStartY
    currentColumn += 1
    if (currentColumn >= columns) {
      doc.addPage()
      drawHeader()
      currentColumn = 0
      yPos = columnStartY
    }
  }

  drawHeader()
  drawSummaryBlock()

  const sortedAgeGroupBreakdown = (ageGroupBreakdown || []).slice().sort((a, b) => {
    const aKey = getAgeGroupSortKey(a.ageGroup)
    const bKey = getAgeGroupSortKey(b.ageGroup)
    if (aKey !== bKey) return aKey - bKey
    return String(a.ageGroup).localeCompare(String(b.ageGroup))
  })

  sortedAgeGroupBreakdown.forEach((agEntry) => {
    const { ageGroup, clubs } = agEntry
    if (!clubs || clubs.length === 0) return

    const lineHeight = 5
    const clubLineHeight = 4
    const headerLines = doc.splitTextToSize(String(ageGroup || ""), columnWidth)
    const neededSpace =
      headerLines.length * lineHeight + lineHeight + clubs.length * clubLineHeight + 6
    checkPageHeight(neededSpace)

    const columnStartX = margin + currentColumn * (columnWidth + columnGap)

    // Age group heading in Museo 700
    doc.setFontSize(ageGroupHeaderFontSize)
    doc.setFont("MuseoSansRounded-700", "normal")
    doc.text(headerLines, columnStartX, yPos)
    yPos += headerLines.length * lineHeight

    // Age-group totals line
    doc.setFontSize(bodyFontSize)
    doc.setFont("MuseoSansRounded-500", "normal")

    const agTotal = clubs.reduce((sum, c) => sum + (c.totalSwimmers || 0), 0)
    const agRelayOnly = clubs.reduce((sum, c) => sum + (c.relayOnlySwimmers || 0), 0)
    const agExhibOnly = clubs.reduce((sum, c) => sum + (c.exhibitionOnlySwimmers || 0), 0)

    const totalParts = [`Total: ${agTotal}`]
    if (agRelayOnly > 0) totalParts.push(`${agRelayOnly} relay-only`)
    if (agExhibOnly > 0) totalParts.push(`${agExhibOnly} exhibition-only`)
    doc.text(totalParts.join("   "), columnStartX, yPos)
    yPos += lineHeight

    // Per-club rows with aligned count columns
    let maxLabelWidth = 0
    clubs.forEach((club) => {
      const label = `  ${club.clubName || `Club ${club.clubId}`}:`
      const w = doc.getTextWidth(label)
      if (w > maxLabelWidth) maxLabelWidth = w
    })
    const numberX = columnStartX + maxLabelWidth + 2

    clubs.forEach((club) => {
      const clubName = club.clubName || `Club ${club.clubId}`
      const label = `  ${clubName}:`
      doc.text(label, columnStartX, yPos)

      const total = club.totalSwimmers || 0
      const relayOnly = club.relayOnlySwimmers || 0
      const exhibOnly = club.exhibitionOnlySwimmers || 0

      const metaParts = []
      if (relayOnly > 0) metaParts.push(`${relayOnly} relay-only`)
      if (exhibOnly > 0) metaParts.push(`${exhibOnly} exhibition-only`)
      const metaSuffix = metaParts.length > 0 ? ` (${metaParts.join(", ")})` : ""
      const valueText = `${total}${metaSuffix}`

      doc.text(valueText, numberX, yPos)
      yPos += clubLineHeight
    })

    yPos += 2
  })

  const fileName = formatFileName(meetName, meetDate)

  const pageCount = doc.getNumberOfPages()
  doc.setFontSize(8)
  doc.setFont("MuseoSansRounded-500", "normal")
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i)
    const pageLabel = `${i} of ${pageCount}`
    const pageWidthLocal = doc.internal.pageSize.getWidth()
    const pageHeightLocal = doc.internal.pageSize.getHeight()
    const x = pageWidthLocal - doc.getTextWidth(pageLabel) - 10
    const y = pageHeightLocal - 8
    doc.text(pageLabel, x, y)
  }

  const blobUrl = doc.output("bloburl")
  if (!options || options.mode !== "previewOnly") {
    doc.save(fileName)
  }

  return blobUrl
}
